export type IpKind = "internal" | "external";

const IPV4_RE =
  /^(?:(?:25[0-5]|2[0-4]\d|1?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|1?\d?\d)$/;

function parseIpv4(ip: string): [number, number, number, number] | null {
  if (!IPV4_RE.test(ip)) return null;
  const parts = ip.split(".").map((part) => Number(part));
  if (parts.length !== 4 || parts.some((part) => !Number.isInteger(part)))
    return null;
  return [parts[0], parts[1], parts[2], parts[3]];
}

function isInternalIpv4(octets: [number, number, number, number]): boolean {
  const [a, b] = octets;
  if (a === 10) return true;
  if (a === 127) return true;
  if (a === 0) return true;
  if (a === 169 && b === 254) return true;
  if (a === 192 && b === 168) return true;
  if (a === 172 && b >= 16 && b <= 31) return true;
  if (a === 100 && b >= 64 && b <= 127) return true;
  return false;
}

function normalizeIpv6(ip: string): string | null {
  const trimmed = ip.trim();
  if (!trimmed.includes(":")) return null;
  const mapped = trimmed.match(/^::ffff:(\d+\.\d+\.\d+\.\d+)$/i);
  if (mapped) return mapped[1];
  return trimmed.toLowerCase();
}

function isInternalIpv6(ip: string): boolean {
  const value = ip.toLowerCase();
  if (value === "::1" || value === "::") return true;
  if (value.startsWith("fe80:")) return true;
  if (value.startsWith("fc") || value.startsWith("fd")) return true;
  return false;
}

export function classifyIp(value: string): IpKind {
  const ip = value.trim();
  const v4 = parseIpv4(ip);
  if (v4) return isInternalIpv4(v4) ? "internal" : "external";

  const v6orMapped = normalizeIpv6(ip);
  if (!v6orMapped) return "external";

  const mappedV4 = parseIpv4(v6orMapped);
  if (mappedV4) return isInternalIpv4(mappedV4) ? "internal" : "external";

  return isInternalIpv6(v6orMapped) ? "internal" : "external";
}

export function isInternalIp(value: string): boolean {
  return classifyIp(value) === "internal";
}
